import React from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import ForumIcon from '@mui/icons-material/Forum';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PaymentsIcon from '@mui/icons-material/Payments';
import PublicIcon from '@mui/icons-material/Public';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';
import TuneIcon from '@mui/icons-material/Tune';

import {
  GroupedNavSection,
  GroupedNavSidebar,
} from '../core/GroupedNavSidebar';

interface Props {
  currentTab: number;
  onChange: (index: number) => void;
  noteCount?: number;
  openTaskCount?: number;
  draftCount?: number;
  certificateCount?: number;
  invoiceCount?: number;
  participantCount?: number;
  openTabletInputCount?: number;
  tabletServiceActive?: boolean;
  pendingCommunication?: boolean;
}

// Tab-Indizes — muessen mit den CustomTabPanel-Indizes in
// LegalTransactionEdit.tsx uebereinstimmen.
export const LT_TAB_INDEX = {
  PROCESS_MANAGER: 0,
  DATA_COLLECTION: 1,
  NOTES: 2,
  TASKS: 3,
  DRAFTS: 4,
  MEETINGS: 5,
  EXECUTION: 6,
  CLIENTS: 7,
  COMMUNICATION: 8,
  CERTIFICATES: 9,
  INVOICES: 10,
  PUBLIC_DOCUMENTS: 11,
  INTERNAL_DOCUMENTS: 12,
  FUNCTIONS: 13,
  RATING: 14,
  TABLET: 15,
  LOG: 16,
} as const;

/* Der Punkt „Log" war bis Phase 7 an `logArray.length > 0` gebunden, also an
   das alte `LegalTransactionLog`: ein dialog- oder importerzeugter Vorgang
   hatte dort nichts stehen und brauchte keinen Menüpunkt. Seit das alte
   Protokoll nur noch gelesen wird, ist diese Liste bei jedem neuen Vorgang
   leer — und hinter demselben Punkt liegt das zentrale Protokoll, das damit
   unerreichbar war. Es hat immer Inhalt, also ist der Punkt immer da (#1330). */
export const LegalTransactionSidebar: React.FC<Props> = ({
  currentTab,
  onChange,
  noteCount = 0,
  openTaskCount = 0,
  draftCount = 0,
  certificateCount = 0,
  invoiceCount = 0,
  participantCount = 0,
  openTabletInputCount = 0,
  tabletServiceActive = false,
  pendingCommunication = false,
}) => {
  const sections: GroupedNavSection<number>[] = [
    {
      title: 'Vollzug',
      items: [
        {
          value: LT_TAB_INDEX.PROCESS_MANAGER,
          label: 'Vorgangsmanager',
          icon: <DashboardIcon />,
        },
        {
          value: LT_TAB_INDEX.DATA_COLLECTION,
          label: 'Datenerfassung',
          icon: <EditNoteIcon />,
        },
        {
          value: LT_TAB_INDEX.EXECUTION,
          label: 'Abwicklung',
          icon: <GavelIcon />,
        },
      ],
    },
    {
      title: 'Beteiligte & Termine',
      items: [
        {
          value: LT_TAB_INDEX.CLIENTS,
          label: 'Mandanten',
          icon: <GroupIcon />,
          badge: participantCount,
        },
        {
          value: LT_TAB_INDEX.MEETINGS,
          label: 'Termine',
          icon: <EventIcon />,
        },
        {
          value: LT_TAB_INDEX.COMMUNICATION,
          label: 'Kommunikation',
          icon: <ForumIcon />,
          badge: pendingCommunication ? 1 : 0,
          badgeColor: 'warning',
        },
      ],
    },
    {
      title: 'Inhalt',
      items: [
        {
          value: LT_TAB_INDEX.NOTES,
          label: 'Notizen',
          icon: <NoteAltIcon />,
          badge: noteCount,
        },
        {
          value: LT_TAB_INDEX.TASKS,
          label: 'Aufgaben',
          icon: <ChecklistIcon />,
          badge: openTaskCount,
          badgeColor: openTaskCount > 0 ? 'error' : 'primary',
        },
      ],
    },
    {
      title: 'Dokumente',
      items: [
        {
          value: LT_TAB_INDEX.DRAFTS,
          label: 'Entwurf',
          icon: <DescriptionIcon />,
          badge: draftCount,
        },
        {
          value: LT_TAB_INDEX.CERTIFICATES,
          label: 'Urkunden',
          icon: <ArticleIcon />,
          badge: certificateCount,
        },
        {
          value: LT_TAB_INDEX.INVOICES,
          label: 'Rechnungen',
          icon: <PaymentsIcon />,
          badge: invoiceCount,
        },
        {
          value: LT_TAB_INDEX.PUBLIC_DOCUMENTS,
          label: 'Öffentliche Dokumente',
          icon: <PublicIcon />,
        },
        {
          value: LT_TAB_INDEX.INTERNAL_DOCUMENTS,
          label: 'Interne Datenablage',
          icon: <FolderIcon />,
        },
      ],
    },
    {
      title: 'Einstellungen',
      items: [
        {
          value: LT_TAB_INDEX.FUNCTIONS,
          label: 'Funktionen',
          icon: <TuneIcon />,
        },
        {
          value: LT_TAB_INDEX.RATING,
          label: 'Bewertungsbögen',
          icon: <AssessmentIcon />,
        },
      ],
    },
    {
      title: 'Ident-Service',
      items: [
        {
          value: LT_TAB_INDEX.TABLET,
          label: 'DiNo Ident-Service',
          icon: <TabletAndroidIcon />,
          badge: openTabletInputCount,
          badgeColor: openTabletInputCount > 0 ? 'error' : 'primary',
          hide: !tabletServiceActive,
        },
      ],
    },
    {
      title: 'System',
      items: [
        {
          value: LT_TAB_INDEX.LOG,
          label: 'Log',
          icon: <HistoryIcon />,
        },
      ],
    },
  ];

  return (
    <GroupedNavSidebar<number>
      value={currentTab}
      onChange={onChange}
      sections={sections}
    />
  );
};
